import React, { useState, useEffect } from 'react';

export default function ProjectStage({ project, isVisible = true }) {
  const [activeImage, setActiveImage] = useState(project.image);

  // Sync active image when project changes
  useEffect(() => {
    setActiveImage(project.image);
  }, [project]);

  return (
    <div
      className="project-stage-container"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 clamp(20px, 4.5vw, 80px)',
        pointerEvents: isVisible ? 'none' : 'none',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(18px) scale(0.97)',
        transition: 'opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
        zIndex: 20,
      }}
    >
      <div
        className="project-stage-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 1.15fr) minmax(320px, 1fr)',
          gap: 'clamp(24px, 4vw, 56px)',
          maxWidth: '1360px',
          width: '100%',
          alignItems: 'center',
        }}
      >
        {/* Left Column: Fixed Project Narrative & Specs */}
        <div
          className="glass-elevated"
          style={{
            padding: 'clamp(24px, 3.5vw, 42px)',
            borderRadius: '24px',
            pointerEvents: isVisible ? 'auto' : 'none',
            border: '1px solid var(--border-strong)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle Accent Ambient Glow behind card */}
          <div
            style={{
              position: 'absolute',
              top: '-40px',
              left: '-40px',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: project.themeColor,
              opacity: 0.14,
              filter: 'blur(55px)',
              pointerEvents: 'none',
            }}
          />

          {/* World & Badge Indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '14px',
              flexWrap: 'wrap',
            }}
          >
            <span
              className="text-caption"
              style={{
                color: project.themeColor,
                fontWeight: 700,
                letterSpacing: '0.12em',
                fontSize: '0.75rem',
              }}
            >
              WORLD {project.index} — {project.worldName.toUpperCase()}
            </span>
            <span
              className="glass-capsule"
              style={{
                padding: '4px 12px',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                background: 'var(--glass-subtle)',
                border: '1px solid var(--border)',
              }}
            >
              {project.badge}
            </span>
          </div>

          {/* Project Title */}
          <h2
            className="text-headline"
            style={{
              fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
              fontWeight: 700,
              letterSpacing: '-0.025em',
              marginBottom: '6px',
              color: 'var(--text-primary)',
            }}
          >
            {project.name}
          </h2>

          {/* Tagline */}
          <p
            style={{
              fontSize: 'clamp(0.98rem, 1.25vw, 1.15rem)',
              color: 'var(--text-secondary)',
              lineHeight: 1.4,
              marginBottom: '14px',
              fontWeight: 400,
            }}
          >
            {project.tagline}
          </p>

          {/* Creator Tag */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '18px',
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: project.themeColor,
              }}
            />
            <span className="text-caption" style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>
              Created by <strong style={{ color: 'var(--text-primary)' }}>{project.creator}</strong> ({project.creatorLocation})
            </span>
          </div>

          {/* Rich Description */}
          <p
            className="text-body"
            style={{
              color: 'var(--text-secondary)',
              marginBottom: '20px',
              lineHeight: 1.6,
              fontSize: '0.9rem',
            }}
          >
            {project.description}
          </p>

          {/* Key Specs Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px',
              marginBottom: '20px',
              background: 'var(--glass-subtle)',
              padding: '14px',
              borderRadius: '14px',
              border: '1px solid var(--border)',
            }}
          >
            {project.specs?.map((spec, i) => (
              <div key={i}>
                <div
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: 'var(--text-muted)',
                    marginBottom: '2px',
                  }}
                >
                  {spec.label}
                </div>
                <div
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}
                >
                  {spec.value}
                </div>
              </div>
            ))}
          </div>

          {/* Supported Platforms */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '24px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginRight: '4px', fontWeight: 600 }}>
              PLATFORMS:
            </span>
            {project.platforms?.map((p, i) => (
              <span
                key={i}
                className="glass-capsule"
                style={{
                  padding: '3px 10px',
                  fontSize: '0.68rem',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                }}
              >
                {p}
              </span>
            ))}
          </div>

          {/* Action CTA */}
          <button
            className="glass-capsule"
            style={{
              width: '100%',
              padding: '16px 28px',
              color: project.themeColor,
              border: `1px solid ${project.themeColor}`,
              letterSpacing: '0.08em',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'var(--glass)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 0 25px ${project.themeColor}33`;
              e.currentTarget.style.background = `${project.themeColor}1a`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.background = 'var(--glass)';
            }}
          >
            {project.actionText}
          </button>
        </div>

        {/* Right Column: Fixed Hero Visual Media Card & Gallery */}
        <div
          className="glass-elevated"
          style={{
            padding: '16px',
            borderRadius: '24px',
            pointerEvents: isVisible ? 'auto' : 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Main Visual Showcase Frame */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 'clamp(260px, 32vh, 420px)',
              borderRadius: '16px',
              overflow: 'hidden',
              background: '#0a0d10',
              border: '1px solid var(--border)',
            }}
          >
            <img
              src={activeImage}
              alt={project.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s ease',
              }}
            />
            {/* Live Indicator Overlay */}
            <div
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                padding: '4px 10px',
                borderRadius: '100px',
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#fff',
              }}
            >
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: project.themeColor,
                  boxShadow: `0 0 8px ${project.themeColor}`,
                }}
              />
              LIVE SHOWCASE
            </div>
          </div>

          {/* Thumbnail Gallery Tabs */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <div
              onClick={() => setActiveImage(project.image)}
              style={{
                flex: 1,
                height: '64px',
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: activeImage === project.image ? `2px solid ${project.themeColor}` : '1px solid var(--border)',
                transition: 'all 0.2s ease',
              }}
            >
              <img src={project.image} alt="Preview 1" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {project.gallery?.map((thumb, idx) => (
              <div
                key={idx}
                onClick={() => setActiveImage(thumb)}
                style={{
                  flex: 1,
                  height: '64px',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: activeImage === thumb ? `2px solid ${project.themeColor}` : '1px solid var(--border)',
                  transition: 'all 0.2s ease',
                }}
              >
                <img src={thumb} alt={`Preview ${idx + 2}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
